import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as randomUUID } from "uuid";
import { Upload } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CreateExportBatchDto, CreateExportBatchDtoDirectionEnum } from "../../../../../api-client";
import {
  useSepaTransactions,
  SepaTransactionFilters,
} from "../../../../../stores/Sepa/sepa.queries";
import {
  useCreateSepaExportBatch,
  useReverseSepaTransaction,
} from "../../../../../stores/Sepa/sepa.mutations";
import { useDownload } from "../../../../../hooks/useDownload";
import { WaitingBackdrop } from "../../../Base/components/BackDrop";
import SepaTransactionTable, { SepaTxnTableRow } from "./SepaTransactionTable";
import CursorLoadMore from "./CursorLoadMore";
import ExportConfirmDialog from "./ExportConfirmDialog";

const fmtEur = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

/** Triggers a browser download/open for a document access URL (no-op if absent). */
const triggerDownload = (url?: string | null) => {
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

interface Props {
  accountId: string;
  /** Direction/status filters applied on top of the account. */
  filters?: Omit<SepaTransactionFilters, "accountId">;
  /** Show selection checkboxes (the PENDING export tabs). */
  selectable?: boolean;
  /** Relabel the export controls for re-exporting returned transactions. */
  reexportMode?: boolean;
  /** Show the "Export" column linking each row to its batch (the Alle tab). */
  showExportColumn?: boolean;
  /** Called when a row's export chip is clicked (e.g. switch to the Exporte tab). */
  onOpenBatch?: (batch: { id: string; batchNumber: number }) => void;
  /** Called when a row body is clicked (e.g. open the originating export timeline). */
  onRowClick?: (row: SepaTxnTableRow) => void;
  /** Enable ↑/↓ row navigation with Enter opening the row's patient (the "Alle" tab). */
  keyboardNav?: boolean;
  /** Show the free-text column (Verwendungszweck / storno reason). */
  showText?: boolean;
  emptyText?: string;
}

/**
 * One tab body: an infinite (cursor) SEPA transaction query wired to the shared
 * table + "Mehr laden". Owns row selection, the Beleg download, the delete flow and
 * (for the PENDING tabs) the export-batch creation.
 */
const SepaTransactionTab: React.FC<Props> = ({
  accountId,
  filters,
  selectable,
  reexportMode,
  showExportColumn,
  onOpenBatch,
  onRowClick,
  keyboardNav,
  showText,
  emptyText,
}) => {
  const query = useSepaTransactions({ accountId, ...filters }, !!accountId);
  const { handleDownloadPdf } = useDownload();
  const reverseTxn = useReverseSepaTransaction();
  const createBatch = useCreateSepaExportBatch();

  const rows = useMemo<SepaTxnTableRow[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingReverse, setPendingReverse] = useState<SepaTxnTableRow | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // One idempotency key per export intent; refreshed each time the dialog opens.
  const idempotencyKeyRef = useRef<string>(randomUUID());
  useEffect(() => {
    if (exportOpen) idempotencyKeyRef.current = randomUUID();
  }, [exportOpen]);

  // Reset the selection whenever the Mandant changes (rows no longer apply).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [accountId]);

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)),
    );

  const openReverse = (row: SepaTxnTableRow) => {
    setReverseReason("");
    setPendingReverse(row);
  };

  const confirmReverse = () => {
    if (!pendingReverse || !reverseReason.trim()) return;
    const id = pendingReverse.id;
    reverseTxn.mutate(
      { id, reason: reverseReason.trim() },
      {
        onSuccess: () =>
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          }),
      },
    );
    setPendingReverse(null);
  };

  // Export summary (only the selected rows).
  const exportDirection = filters?.direction as CreateExportBatchDtoDirectionEnum | undefined;
  const canExport = !!(selectable && exportDirection);
  const selectedTotal = useMemo(
    () => rows.reduce((sum, r) => (selectedIds.has(r.id) ? sum + Number(r.amount) : sum), 0),
    [rows, selectedIds],
  );
  const itemLabel =
    exportDirection === CreateExportBatchDtoDirectionEnum.DirectDebit
      ? "Lastschriften"
      : "Rückerstattungen";

  const confirmExport = () => {
    if (!exportDirection || selectedIds.size === 0) return;
    const dto: CreateExportBatchDto = {
      accountId,
      direction: exportDirection,
      transactionIds: Array.from(selectedIds),
      idempotencyKey: idempotencyKeyRef.current,
    };
    createBatch.mutate(dto, {
      onSuccess: (batch) => {
        setSelectedIds(new Set());
        setExportOpen(false);
        // Auto-download the generated SEPA XML + summary PDF (access URLs come back
        // on the export-create response). The download icons remain for later access.
        const docs = batch as {
          xmlDocument?: { accessUrl?: string };
          pdfDocument?: { accessUrl?: string };
        };
        triggerDownload(docs.xmlDocument?.accessUrl);
        triggerDownload(docs.pdfDocument?.accessUrl);
      },
    });
  };

  return (
    <div className="space-y-2">
      <WaitingBackdrop isWorking={createBatch.isPending} message="SEPA-Export wird erstellt..." />

      {canExport && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-solid border-slate-200 bg-slate-50/60 px-4 py-2">
          <p className="text-sm font-medium text-slate-600">
            {selectedIds.size > 0
              ? `${selectedIds.size} ausgewählt · Summe ${fmtEur(selectedTotal)}`
              : reexportMode
                ? "Transaktionen zum erneuten Export auswählen"
                : "Transaktionen zum Export auswählen"}
          </p>
          <Button
            className="bg-blue-darkest text-sm font-semibold text-white hover:bg-blue-darkest/90"
            disabled={selectedIds.size === 0}
            onClick={() => setExportOpen(true)}
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {reexportMode ? "Erneut exportieren" : "Exportieren"}
          </Button>
        </div>
      )}

      <div
        ref={scrollRef}
        className="thin-scrollbar max-h-[860px] overflow-y-auto rounded-lg border border-solid border-slate-200"
      >
        <SepaTransactionTable
          rows={rows}
          selectable={selectable}
          showExportColumn={showExportColumn}
          onOpenBatch={onOpenBatch}
          selectedIds={selectedIds}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onDownloadDocument={handleDownloadPdf}
          onReverse={openReverse}
          onRowClick={onRowClick}
          keyboardNav={keyboardNav}
          showText={showText}
          busy={reverseTxn.isPending || createBatch.isPending}
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          emptyText={emptyText}
          bare
        />

        <CursorLoadMore
          hasNextPage={!!query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => query.fetchNextPage()}
          scrollRootRef={scrollRef}
        />
      </div>

      <ExportConfirmDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        itemLabel={itemLabel}
        count={selectedIds.size}
        totalAmount={selectedTotal}
        isPending={createBatch.isPending}
        onConfirm={confirmExport}
        reexportMode={reexportMode}
      />

      <AlertDialog
        open={!!pendingReverse}
        onOpenChange={(open) => !open && setPendingReverse(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transaktion stornieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReverse
                ? `Die Transaktion ${pendingReverse.transactionNumber} bleibt sichtbar und wird als storniert markiert. Eine Stornobuchung mit Gegenbetrag wird erstellt.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            rows={3}
            placeholder="Stornogrund"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={!reverseReason.trim() || reverseTxn.isPending}
              onClick={confirmReverse}
            >
              Stornieren
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SepaTransactionTab;
